import {
  DragRef,
  useDragDropContext,
} from '@wandb/weave/common/containers/DragDropContainer';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {usePanelSearchDebouncedContext} from '../../../state/panelbank/PanelSearchContextProvider';
import * as ViewHooks from '../../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {isPanelBankSection} from '../../../util/panelbank';
import {makeContext} from '../../MakeContext/makeContext';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {useVisibleSectionsContext} from './useVisibleSectionsContext';

const MAX_AUTO_OPEN_SECTIONS = 20;

export function isSectionOpen(
  __id__: string,
  isOpen: boolean,
  isSearching: boolean,
  isSectionOpenWhileSearching: (
    sectionId: string,
    persistedValue: boolean
  ) => boolean,
  dragRef: DragRef | null,
  shiftKey: boolean
): boolean {
  const isOpenWhileSearching = isSectionOpenWhileSearching(__id__, isOpen);
  const isDraggingSection = dragRef != null && isPanelBankSection(dragRef);
  const forceCollapsed = isDraggingSection || shiftKey;

  // This is the final section open/close state (getter and setter) used in the UI
  const sectionIsOpen =
    !forceCollapsed && (isSearching ? isOpenWhileSearching : isOpen);
  return sectionIsOpen;
}

// This controls the open/close state of a single section in the panel bank.
// This state is derived from various sources, see inline comments.
export const usePanelBankSectionIsOpen = ({
  panelBankSectionConfigRef,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
}) => {
  // isOpen and toggleIsOpen are for when no search is active
  const {__id__, isOpen} = ViewHooks.usePart(panelBankSectionConfigRef);
  const toggleIsOpen = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.toggleIsOpen
  );

  // isOpenWhileSearching is a temporary override used during panel search
  const {isSearching} = usePanelSearchDebouncedContext();
  const {isSectionOpenWhileSearching, toggleIsOpenTemp} =
    usePanelBankSectionsOpenTempContext();
  const toggleIsOpenWhileSearching = useCallback(() => {
    toggleIsOpenTemp(__id__);
  }, [__id__, toggleIsOpenTemp]);

  // Another override!
  // If we're dragging a section, or dragging a panel while holding the shift key, collapse all sections temporarily.
  const {dragRef, shiftKey} = useDragDropContext();
  const sectionIsOpen = isSectionOpen(
    __id__,
    isOpen,
    isSearching,
    isSectionOpenWhileSearching,
    dragRef,
    shiftKey
  );

  const toggleSectionIsOpen = isSearching
    ? toggleIsOpenWhileSearching
    : toggleIsOpen;

  // One more thing...
  // We don't want to render collapsed sections until they're first opened,
  // but we also don't want to re-mount sections (and re-fetch panels) if you collapse and re-open them.
  // So we use this state variable, which flips to 'true' the first time the section is opened.
  const [shouldRenderSection, setShouldRenderSection] = useState(false);
  useEffect(() => {
    if (shouldRenderSection) {
      return;
    }
    if (sectionIsOpen) {
      setShouldRenderSection(sectionIsOpen);
    }
  }, [sectionIsOpen, shouldRenderSection]);

  return useMemo(
    () => ({
      sectionIsOpen,
      toggleSectionIsOpen,
      shouldRenderSection,
    }),
    [sectionIsOpen, shouldRenderSection, toggleSectionIsOpen]
  );
};

/**
 * This hook provides a way for sections to know if they are open during panel search.
 * Users can still toggle sections during search, and these changes will be saved
 * to the workspace spec. However, this is a temporary state that will be reset if
 * the search is cleared.
 */
export const usePanelBankSectionsOpenTemp = () => {
  const {panelBankActions, panelBankConfigRef} = useWorkspaceRefsContext();
  const panelBankConfigPart = ViewHooks.usePart(panelBankConfigRef);

  const {autoExpandSearchResults, searchSectionsOpen} =
    panelBankConfigPart.settings;
  const visibleSections = useVisibleSectionsContext();

  const autoExpandableSectionIds = useMemo(() => {
    const sectionIdsArr = visibleSections
      .slice(0, MAX_AUTO_OPEN_SECTIONS)
      .map(section => section.__id__);
    return new Set(sectionIdsArr);
  }, [visibleSections]);

  // Because there are workspaces with large amounts of sections (25k),
  // we don't want to store state for all sections by default. Instead
  // we track a baseline mode, and then the specific sections that
  // have been toggled to be different than all the rest.
  const mode =
    searchSectionsOpen?.mode ??
    (autoExpandSearchResults ? 'auto-expand' : 'default');
  const overrides = useMemo(
    () => new Set(searchSectionsOpen?.overrides ?? []),
    [searchSectionsOpen?.overrides]
  );

  const toggleIsOpenTemp = useCallback(
    (sectionId: string) => {
      const next = new Set(overrides);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      panelBankActions.updatePanelBankSettings({
        searchSectionsOpen: {
          mode,
          overrides: next.size ? [...next] : undefined,
        },
      });
    },
    [mode, overrides, panelBankActions]
  );

  const openAllSectionsTemp = useCallback(() => {
    panelBankActions.updatePanelBankSettings({
      searchSectionsOpen: {
        mode: 'expand-all',
        overrides: undefined,
      },
    });
  }, [panelBankActions]);
  const closeAllSectionsTemp = useCallback(() => {
    panelBankActions.updatePanelBankSettings({
      searchSectionsOpen: {
        mode: 'collapse-all',
        overrides: undefined,
      },
    });
  }, [panelBankActions]);

  const isSectionOpenWhileSearching = useCallback(
    (sectionId: string, persistedValue: boolean) => {
      let baselineValue = persistedValue;
      if (mode === 'auto-expand') {
        baselineValue = autoExpandableSectionIds.has(sectionId);
      } else if (mode === 'expand-all') {
        baselineValue = true;
      } else if (mode === 'collapse-all') {
        baselineValue = false;
      }
      const isOverridden = overrides.has(sectionId);
      return isOverridden ? !baselineValue : baselineValue;
    },
    [autoExpandableSectionIds, mode, overrides]
  );

  return useMemo(
    () => ({
      toggleIsOpenTemp,
      openAllSectionsTemp,
      closeAllSectionsTemp,
      isSectionOpenWhileSearching,
    }),
    [
      closeAllSectionsTemp,
      openAllSectionsTemp,
      isSectionOpenWhileSearching,
      toggleIsOpenTemp,
    ]
  );
};

// Context provider and hook for usePanelBankSectionsOpenTemp
export const {
  Provider: PanelBankSectionsOpenTempProvider,
  hook: usePanelBankSectionsOpenTempContext,
} = makeContext<ReturnType<typeof usePanelBankSectionsOpenTemp>>({
  contextType: 'PanelBankSectionsOpenTemp',
  getContextValue: usePanelBankSectionsOpenTemp,
});
